import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DaysDropdown from './DaysDropdown';
import CoachesDropdown from './CoachesDropdown';
import { db } from "../firebase/fire";
import { collection, doc, updateDoc, addDoc, deleteDoc, getDocs, writeBatch, getDoc } from "firebase/firestore";
import LoadingOverlay from './LoadingOverlay';
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaClock, FaUsers, FaCalendarDay, FaUserTie, FaChair, FaCheckCircle } from 'react-icons/fa';



export default function ClassSchedule
({ classSchedule, setClassSchedule, coaches, isSecAdmin, theme = 'light' }) {
    const [editingClass, setEditingClass] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [newClass, setNewClass] = useState({
        className: "",
        startTime: "",
        endTime: "",
        days: [],
        coaches: [],
        availableSpots: 0,
        isSpecialClass: false,
        specialClassDate: null,
        isActive: true,
        isAttendanceReminder: true,
        allAttend: false,
    });

    const regularClasses = classSchedule.filter(c => !c.isSpecialClass);
    const specialClasses = classSchedule.filter(c => c.isSpecialClass);

    // Sort regular classes by days
    const dayOrder = {
        Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7
    };

    regularClasses.sort((a, b) => {
        const aMinDay = Math.min(...a.days.map(day => dayOrder[day] || 99));
        const bMinDay = Math.min(...b.days.map(day => dayOrder[day] || 99));
        return aMinDay - bMinDay;
    });

    const addClassToAllMembers = async (classId) => {
        const membersSnapshot = await getDocs(collection(db, "members"));
        const batch = writeBatch(db);

        membersSnapshot.forEach(docSnap => {
            const member = docSnap.data();
            const eligibleClasses = Array.isArray(member.eligibleClasses) ? member.eligibleClasses : [];
            if (!eligibleClasses.includes(classId)) {
                batch.update(doc(db, "members", docSnap.id), {
                    eligibleClasses: [...eligibleClasses, classId]
                });
            }
        });

        await batch.commit();
    };

    const removeClassFromAllMembers = async (classId) => {
        const membersSnapshot = await getDocs(collection(db, "members"));
        const batch = writeBatch(db);

        membersSnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const eligible = Array.isArray(data.eligibleClasses)
                ? data.eligibleClasses
                : [];
            if (eligible.includes(classId)) {
                batch.update(doc(db, "members", docSnap.id), {
                    eligibleClasses: eligible.filter((id) => id !== classId),
                });
            }
        });

        await batch.commit();
    };

    const handleAddOrUpdateClass = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            // Check for duplicate classes
            const isDuplicate = classSchedule.some(cls =>
                cls.className === newClass.className &&
                cls.startTime === newClass.startTime &&
                cls.endTime === newClass.endTime &&
                (newClass.isSpecialClass
                    ? cls.specialClassDate === newClass.specialClassDate
                    : cls.days.some(day => newClass.days.includes(day))) &&
                cls.id !== (editingClass ? editingClass.id : null)
            );

            if (isDuplicate) {
                alert("A class with the same name, time, and day/date already exists.");
                setIsLoading(false);
                return;
            }

            const classData = {
                ...newClass,
                days: newClass.isSpecialClass ? [] : newClass.days,
                specialClassDate: newClass.isSpecialClass ? newClass.specialClassDate : null,
                allAttend: newClass.allAttend
            };

            if (editingClass) {
                await updateDoc(doc(db, "classSchedule", editingClass.id), classData);
                setClassSchedule(classSchedule.map(cls => cls.id === editingClass.id ? { ...cls, ...classData } : cls));
                
                if (editingClass.allAttend && !classData.allAttend) {
                    await removeClassFromAllMembers(editingClass.id);
                }
                if (!editingClass.allAttend && classData.allAttend) {
                    await addClassToAllMembers(editingClass.id);
                }
                setEditingClass(null);
            } else {
                const docRef = await addDoc(collection(db, "classSchedule"), classData);
                setClassSchedule([...classSchedule, { id: docRef.id, ...classData }]);
                if (classData.allAttend) {
                    await addClassToAllMembers(docRef.id);
                }
            }

            setNewClass({
                className: "",
                startTime: "",
                endTime: "",
                days: [],
                coaches: [],
                availableSpots: 0,
                isSpecialClass: false,
                specialClassDate: null,
                isActive: true,
                isAttendanceReminder: true,
                allAttend: false
            });
            setIsLoading(false);
        } catch (error) {
            console.error("Error adding/updating class: ", error);
            setIsLoading(false);
        }
    };

    const timeStringToDate = (time) => {
        if (!time) return null;
        const parts = time.split(':');
        if (parts.length < 2) return null;
        const hh = parseInt(parts[0], 10);
        const mm = parseInt(parts[1], 10) || 0;
        const d = new Date();
        d.setHours(hh, mm, 0, 0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        return d;
    };

    const dateToTimeString = (date) => {
        if (!date) return '';
        const hh = date.getHours();
        const mm = date.getMinutes();
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    };

    const toAmPm = (time) => {
        if (!time) return 'AM';
        const parts = time.split(':');
        if (parts.length < 2) return 'AM';
        const hh = parseInt(parts[0], 10);
        return hh >= 12 ? 'PM' : 'AM';
    };

    const applyAmPm = (time, ampm) => {
        const fallback = '00:00';
        const t = time || fallback;
        const parts = t.split(':');
        if (parts.length < 2) return t;
        let hh = parseInt(parts[0], 10);
        const mm = parts[1];
        if (ampm === 'AM') {
            if (hh === 12) hh = 0;
        } else {
            if (hh < 12) hh = hh + 12;
        }
        return `${String(hh).padStart(2, '0')}:${mm}`;
    };

    const handleEditClass = (classItem) => {
        setEditingClass(classItem);
    };

    const handleSaveClass = async (classId, updatedClass) => {
        try {
            setIsLoading(true);
            const classRef = doc(db, "classSchedule", classId);
            const prevClass = classSchedule.find(cls => cls.id === classId);

            // Filter out undefined values to avoid Firestore errors
            const cleanedClass = Object.fromEntries(
                Object.entries(updatedClass).filter(([_, v]) => v !== undefined)
            );

            await updateDoc(classRef, cleanedClass);

            setClassSchedule(classSchedule.map(cls =>
                cls.id === classId ? { ...cls, ...cleanedClass } : cls
            ));

            // Handle All Attend logic
            if (prevClass && prevClass.allAttend !== cleanedClass.allAttend) {
                const membersSnapshot = await getDocs(collection(db, "members"));
                const batch = writeBatch(db);

                membersSnapshot.forEach((docSnap) => {
                    const member = docSnap.data();
                    const eligible = Array.isArray(member.eligibleClasses)
                        ? member.eligibleClasses
                        : [];

                    if (cleanedClass.allAttend) {
                        if (!eligible.includes(classId)) {
                            batch.update(doc(db, "members", docSnap.id), {
                                eligibleClasses: [...eligible, classId]
                            });
                        }
                    } else {
                        if (eligible.includes(classId)) {
                            batch.update(doc(db, "members", docSnap.id), {
                                eligibleClasses: eligible.filter((id) => id !== classId)
                            });
                        }
                    }
                });

                await batch.commit();
            }

            setEditingClass(null);
            setIsLoading(false);
        } catch (error) {
            console.error("Error updating class:", error);
            setIsLoading(false);
        }
    };

    const handleToggleClass = async (classId) => {
        try {
            const classRef = doc(db, "classSchedule", classId);
            const classDoc = await getDoc(classRef);
            const currentClass = classDoc.data();

            await updateDoc(classRef, {
                isActive: !currentClass.isActive
            });

            setClassSchedule(prevSchedule =>
                prevSchedule.map(c => c.id === classId ? { ...c, isActive: !c.isActive } : c)
            );
        } catch (error) {
            console.error("Error toggling class status:", error);
        }
    };

    const handleToggleAttendanceReminder = async (classId) => {
        try {
            const classRef = doc(db, "classSchedule", classId);
            const classDoc = await getDoc(classRef);
            const currentClass = classDoc.data();

            await updateDoc(classRef, {
                isAttendanceReminder: !currentClass.isAttendanceReminder
            });

            setClassSchedule(prevSchedule =>
                prevSchedule.map(c => c.id === classId ? { ...c, isAttendanceReminder: !c.isAttendanceReminder } : c)
            );
        } catch (error) {
            console.error("Error toggling:", error);
        }
    };

    const handleDeleteClass = async (classId) => {
        const classToDelete = classSchedule.find(c => c.id === classId);
        const className = classToDelete?.className || 'this class';
        if (window.confirm(`Are you sure you want to delete class "${className}"?`)) {
            try {
                await deleteDoc(doc(db, "classSchedule", classId));
                setClassSchedule(classSchedule.filter(classItem => classItem.id !== classId));
            } catch (error) {
                console.error("Error deleting class: ", error);
            }
        }
    };

    const formatTimeDisplay = (time) => {
        if (!time) return '';
        const parts = time.split(':');
        if (parts.length < 2) return time;
        let hh = parseInt(parts[0], 10);
        const mm = parts[1];
        const ampm = hh >= 12 ? 'PM' : 'AM';
        if (hh > 12) hh -= 12;
        if (hh === 0) hh = 12;
        return `${hh}:${mm} ${ampm}`;
    };

    const ClassRow = ({
        classItem,
        editingClass,
        setEditingClass,
        coaches,
        isSpecial,
        handleSaveClass,
        handleDeleteClass,
        isSecAdmin,
        handleToggleClass,
        handleToggleAttendanceReminder
    }) => {
        const isEditing = editingClass && editingClass.id === classItem.id;

        const [localClassName, setLocalClassName] = React.useState(classItem.className);
        const [localStartTime, setLocalStartTime] = React.useState(classItem.startTime);
        const [localEndTime, setLocalEndTime] = React.useState(classItem.endTime);
        const [localDays, setLocalDays] = React.useState(classItem.days || []);
        const [localSpecialClassDate, setLocalSpecialClassDate] = React.useState(classItem.specialClassDate || '');
        const [localCoaches, setLocalCoaches] = React.useState(classItem.coaches || []);
        const [localAvailableSpots, setLocalAvailableSpots] = React.useState(classItem.availableSpots);
        const [localIsSpecialClass, setLocalIsSpecialClass] = React.useState(classItem.isSpecialClass);
        const [localIsActive, setLocalIsActive] = React.useState(classItem.isActive);
        const [localIsAttendanceReminder, setLocalIsAttendanceReminder] = React.useState(classItem.isAttendanceReminder);
        const [localAllAttend, setLocalAllAttend] = React.useState(classItem.allAttend);

        React.useEffect(() => {
            if (isEditing) {
                setLocalClassName(classItem.className);
                setLocalStartTime(classItem.startTime);
                setLocalEndTime(classItem.endTime);
                setLocalDays(classItem.days || []);
                setLocalSpecialClassDate(classItem.specialClassDate || '');
                setLocalCoaches(classItem.coaches || []);
                setLocalAvailableSpots(classItem.availableSpots);
                setLocalIsSpecialClass(classItem.isSpecialClass);
                setLocalIsActive(classItem.isActive);
                setLocalIsAttendanceReminder(classItem.isAttendanceReminder);
                setLocalAllAttend(classItem.allAttend);
            }
        }, [isEditing, classItem]);

        const save = () => {
            handleSaveClass(classItem.id, {
                className: localClassName,
                startTime: localStartTime,
                endTime: localEndTime,
                days: localIsSpecialClass ? [] : localDays,
                specialClassDate: localIsSpecialClass ? localSpecialClassDate : null,
                coaches: localCoaches,
                availableSpots: localAvailableSpots,
                isSpecialClass: localIsSpecialClass,
                isActive: localIsActive,
                isAttendanceReminder: localIsAttendanceReminder,
                allAttend: localAllAttend
            });
        };

        const toggleDay = (day) => {
            setLocalDays(prev =>
                prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
            );
        };

        return (
            <tr className={`border-b ${
                theme === 'dark' ? 'border-gray-700 hover:bg-gray-700' : 'hover:bg-gray-50'
            }`}>
                <td className={`border p-2 font-semibold font-sans text-sm sm:text-base ${
                    theme === 'dark' ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-800'
                }`}>
                    {isEditing ? (
                        <input
                            type="text"
                            value={localClassName}
                            onChange={(e) => setLocalClassName(e.target.value)}
                            className={`w-full p-1 border rounded ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    ) : (
                        classItem.className
                    )}
                </td>
                <td className={`border p-2 min-w-[200px] font-mono text-sm ${
                    theme === 'dark' ? 'border-gray-700 text-gray-200' : 'border-gray-300 text-gray-700'
                }`}>
                    {isEditing ? (
                        <div className="flex gap-2 items-center">
                            <DatePicker
                                selected={timeStringToDate(localStartTime)}
                                onChange={(date) => setLocalStartTime(dateToTimeString(date))}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={5}
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                className={`w-20 p-1 border rounded text-xs ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                            <span>to</span>
                            <DatePicker
                                selected={timeStringToDate(localEndTime)}
                                onChange={(date) => setLocalEndTime(dateToTimeString(date))}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={5}
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                className={`w-20 p-1 border rounded text-xs ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                        </div>
                    ) : (
                        `${formatTimeDisplay(classItem.startTime)} - ${formatTimeDisplay(classItem.endTime)}`
                    )}
                </td>
                <td className={`border p-2 ${
                    theme === 'dark' ? 'border-gray-700 text-gray-200' : 'border-gray-300 text-gray-700'
                }`}>
                    {isEditing ? (
                        localIsSpecialClass ? (
                            <input
                                type="date"
                                value={localSpecialClassDate || ''}
                                onChange={(e) => setLocalSpecialClassDate(e.target.value)}
                                className={`p-1 border rounded w-full ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                        ) : (
                            <div className="flex flex-col gap-1">
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                    <label key={day} className="flex items-center text-xs">
                                        <input
                                            type="checkbox"
                                            checked={localDays.includes(day)}
                                            onChange={() => toggleDay(day)}
                                            className="mr-2"
                                        />
                                        {day}
                                    </label>
                                ))}
                            </div>
                        )
                    ) : (
                        classItem.isSpecialClass
                            ? (classItem.specialClassDate || 'N/A')
                            : (classItem.days?.join(", ") || 'N/A')
                    )}
                </td>
                <td className={`border p-2 font-sans text-sm ${
                    theme === 'dark' ? 'border-gray-700 text-gray-200' : 'border-gray-300 text-gray-700'
                }`}>
                    {isEditing ? (
                        <div className={`max-h-32 overflow-y-auto border rounded p-1 ${
                            theme === 'dark'
                                ? 'bg-gray-700 border-gray-600'
                                : 'bg-white border-gray-300'
                        }`}>
                            {coaches.map(coach => (
                                <label key={coach.id} className="flex items-center text-xs mb-1">
                                    <input
                                        type="checkbox"
                                        checked={localCoaches.includes(coach.name)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setLocalCoaches([...localCoaches, coach.name]);
                                            } else {
                                                setLocalCoaches(localCoaches.filter(c => c !== coach.name));
                                            }
                                        }}
                                        className="mr-2"
                                    />
                                    {coach.name}
                                </label>
                            ))}
                        </div>
                    ) : (
                        classItem.coaches?.join(", ") || 'N/A'
                    )}
                </td>
                <td className="border p-2 text-center font-bold text-blue-600 text-sm sm:text-base">
                    {isEditing ? (
                        <input
                            type="number"
                            value={localAvailableSpots}
                            onChange={(e) => setLocalAvailableSpots(parseInt(e.target.value, 10))}
                            className={`w-20 p-1 border rounded ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    ) : (
                        classItem.availableSpots
                    )}
                </td>
                <td className={`border p-2 text-center font-sans text-sm ${
                    theme === 'dark' ? 'border-gray-700 text-gray-200' : 'border-gray-300 text-gray-700'
                }`}>
                    {isEditing ? (
                        <select
                            value={localIsSpecialClass ? 'yes' : 'no'}
                            onChange={(e) => setLocalIsSpecialClass(e.target.value === 'yes')}
                            className={`p-1 border rounded ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                        </select>
                    ) : (
                        classItem.isSpecialClass ? 'Yes' : 'No'
                    )}
                </td>
                
                <td className="border p-2 text-center">
                    <label className="flex items-center justify-center cursor-pointer">
                        <div className="relative">
                            <input
                                type="checkbox"
                                className="sr-only"
                                checked={classItem.isActive}
                                disabled={isSecAdmin}
                                onChange={() => handleToggleClass(classItem.id)}
                            />
                            <div className={`block w-10 h-6 rounded-full ${classItem.isActive ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${classItem.isActive ? 'transform translate-x-4' : ''}`}></div>
                        </div>
                    </label>
                </td>
                <td className="border p-2 text-center">
                    <label className="flex items-center justify-center cursor-pointer">
                        <div className="relative">
                            <input
                                type="checkbox"
                                className="sr-only"
                                checked={classItem.isAttendanceReminder}
                                disabled={isSecAdmin}
                                onChange={() => handleToggleAttendanceReminder(classItem.id)}
                            />
                            <div className={`block w-10 h-6 rounded-full ${classItem.isAttendanceReminder ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${classItem.isAttendanceReminder ? 'transform translate-x-4' : ''}`}></div>
                        </div>
                    </label>
                </td>
                <td className={`border p-2 text-center font-medium text-sm ${
                    theme === 'dark' ? 'border-gray-700 text-gray-200' : 'border-gray-300 text-gray-700'
                }`}>
                    {isEditing ? (
                        <select
                            value={localAllAttend ? 'yes' : 'no'}
                            onChange={(e) => setLocalAllAttend(e.target.value === 'yes')}
                            className={`p-1 border rounded ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                        </select>
                    ) : (
                        classItem.allAttend ? 'Yes' : 'No'
                    )}
                </td>
                <td className="border p-2">
                    {isEditing ? (
                        <div className="flex gap-2">
                            <button
                                onClick={save}
                                disabled={isSecAdmin}
                                className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 disabled:opacity-50"
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setEditingClass(null)}
                                className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setEditingClass(classItem)}
                                disabled={isSecAdmin}
                                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                            >
                                <FaEdit />
                                Edit
                            </button>
                            <button
                                onClick={() => handleDeleteClass(classItem.id)}
                                disabled={isSecAdmin}
                                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                            >
                                <FaTrash />
                                Delete
                            </button>
                        </div>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <LoadingOverlay isLoading={isLoading}>
            <div className={`p-4 sm:p-6 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                <div>
                    {/* <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-6 sm:p-8 mb-6">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-3">
                            <FaCalendarAlt />
                            Class Schedule
                        </h2>
                        <p className="text-blue-100 text-sm sm:text-base">Manage regular and special classes</p> 
                    </div> */}

                    <form onSubmit={handleAddOrUpdateClass} className={`shadow-xl rounded-xl overflow-hidden border mb-8 ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <FaPlus className="text-white" />
                            </div>
                            Add New Class
                        </h3>
                        <p className="text-blue-50 text-sm mt-1">Fill in the details to create a new class schedule</p>
                    </div>
                    <div className="p-6 sm:p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                        <div className="w-full">
                            <label htmlFor="className" className={`block text-sm sm:text-base font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                <FaCalendarAlt className="inline mr-2 text-blue-600" />
                                Class Name
                            </label>
                            <input
                                id="className"
                                type="text"
                                name="className"
                                placeholder="e.g., Yoga Class"
                                value={newClass.className}
                                disabled={isSecAdmin}
                                onChange={(e) => setNewClass({ ...newClass, className: e.target.value })}
                                required
                                className={`p-2 border-2 rounded-lg w-full text-sm sm:text-base focus:border-blue-500 focus:outline-none transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                        </div>
                        <div className="w-full">
                            <label htmlFor="startTime" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                                <FaClock className="inline mr-2 text-blue-600" />
                                Start Time
                            </label>
                            <DatePicker
                                selected={timeStringToDate(newClass.startTime)}
                                onChange={(date) => setNewClass({ ...newClass, startTime: dateToTimeString(date) })}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={5}
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                wrapperClassName="w-full"
                                className={`p-2 border-2 rounded-lg w-full text-sm focus:border-blue-500 focus:outline-none transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                                calendarClassName={theme === 'dark' ? 'dark-calendar' : ''}
                                disabled={isSecAdmin}
                                placeholderText="Select start time"
                            />
                        </div>
                        <div className="w-full">
                            <label htmlFor="endTime" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                                <FaClock className="inline mr-2 text-blue-600" />
                                End Time
                            </label>
                            <DatePicker
                                selected={timeStringToDate(newClass.endTime)}
                                onChange={(date) => setNewClass({ ...newClass, endTime: dateToTimeString(date) })}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={5}
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                wrapperClassName="w-full"
                                className={`p-2 border-2 rounded-lg w-full text-sm focus:border-blue-500 focus:outline-none transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                                calendarClassName={theme === 'dark' ? 'dark-calendar' : ''}
                                disabled={isSecAdmin}
                                placeholderText="Select end time"
                            />
                        </div>
                        <div className="w-full">
                            <label htmlFor="isSpecialClass" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                                <FaCheckCircle className="inline mr-2 text-blue-600" />
                                Special Class
                            </label>
                            <select
                                id="isSpecialClass"
                                name="isSpecialClass"
                                value={newClass.isSpecialClass ? 'yes' : 'no'}
                                disabled={isSecAdmin}
                                onChange={(e) => {
                                    const isSpecial = e.target.value === 'yes';
                                    setNewClass({
                                        ...newClass,
                                        isSpecialClass: isSpecial,
                                        days: isSpecial ? [] : newClass.days
                                    });
                                }}
                                className={`p-2 border-2 rounded-lg w-full text-sm focus:border-blue-500 focus:outline-none transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                            </select>
                        </div>
                        {newClass.isSpecialClass ? (
                            <div className="w-full">
                                <label htmlFor="specialClassDate" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                }`}>
                                    <FaCalendarDay className="inline mr-2 text-blue-600" />
                                    Special Class Date
                                </label>
                                <input
                                    id="specialClassDate"
                                    type="date"
                                    name="specialClassDate"
                                    disabled={isSecAdmin}
                                    value={newClass.specialClassDate || ''}
                                    onChange={(e) => setNewClass({ ...newClass, specialClassDate: e.target.value })}
                                    required={newClass.isSpecialClass}
                                    className={`p-2 border-2 rounded-lg w-full text-sm focus:border-blue-500 focus:outline-none transition-colors ${
                                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                />
                            </div>
                        ) : (
                            <div className="w-full">
                                <label htmlFor="days" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                }`}>
                                    <FaCalendarDay className="inline mr-2 text-blue-600" />
                                    Days
                                </label>
                                <DaysDropdown
                                    id="days"
                                    isSecAdmin={isSecAdmin}
                                    selectedDays={newClass.days}
                                    onChange={(days) => setNewClass({ ...newClass, days })}
                                    disabled={newClass.isSpecialClass}
                                    theme={theme}
                                />
                            </div>
                        )}
                        
                        <div className="w-full">
                            <label className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                                <FaUserTie className="inline mr-2 text-blue-600" />
                                Instructors
                            </label>
                            <CoachesDropdown
                                coaches={coaches}
                                selectedCoaches={newClass.coaches}
                                isSecAdmin={isSecAdmin}
                                onChange={(selectedCoaches) => setNewClass({ ...newClass, coaches: selectedCoaches })}
                                theme={theme}
                            />
                        </div>
                        <div className="w-full">
                            <label htmlFor="availableSpots" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                                <FaChair className="inline mr-2 text-blue-600" />
                                Available Spots
                            </label>
                            <input
                                id="availableSpots"
                                type="number"
                                name="availableSpots"
                                placeholder="Number of spots"
                                value={newClass.availableSpots}
                                disabled={isSecAdmin}
                                onChange={(e) => setNewClass({ ...newClass, availableSpots: parseInt(e.target.value, 10) })}
                                required
                                className={`p-2 border-2 rounded-lg w-full text-sm focus:border-blue-500 focus:outline-none transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                        </div>
                        <div className="w-full">
                            <label htmlFor="allAttend" className={`block text-xs sm:text-sm font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                                <FaUsers className="inline mr-2 text-blue-600" />
                                All Attend
                            </label>
                            <select
                                id="allAttend"
                                name="allAttend"
                                value={newClass.allAttend ? 'yes' : 'no'}
                                disabled={isSecAdmin}
                                onChange={(e) => {
                                    const allAttend = e.target.value === 'yes';
                                    setNewClass({
                                        ...newClass,
                                        allAttend: allAttend,
                                    });
                                }}
                                className={`p-2 border-2 rounded-lg w-full text-sm focus:border-blue-500 focus:outline-none transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                            </select>
                        </div>
                    </div>
                    <div className={`mt-6 pt-4 border-t ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <button 
                            type="submit" 
                            disabled={isSecAdmin}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-sm sm:text-base flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <FaPlus className="text-lg" />
                            Add Class
                        </button>
                    </div>
                    </div>
                </form>

                {/* Regular Classes Table */}
                <div className={`shadow-xl rounded-xl overflow-hidden border mb-8 ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className={`px-6 py-4 ${
                        theme === 'dark' ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700'
                    }`}>
                        <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <FaCalendarAlt className="text-white" />
                            </div>
                            Regular Classes
                        </h3>
                        <p className="text-blue-50 text-sm mt-1">Manage recurring class schedules</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm sm:text-base">
                            <thead>
                                <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Class Name</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Time</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Days</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Instructor</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Spots</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Special Class</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Instructor Log in</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Attendance Check</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">All Attend</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${
                                    theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'
                                }`}>
                                    {regularClasses.map(classItem => (
                                        <ClassRow
                                            key={classItem.id}
                                            classItem={classItem}
                                            editingClass={editingClass}
                                            setEditingClass={setEditingClass}
                                            coaches={coaches}
                                            isSpecial={false}
                                            handleSaveClass={handleSaveClass}
                                            handleDeleteClass={handleDeleteClass}
                                            isSecAdmin={isSecAdmin}
                                            handleToggleClass={handleToggleClass}
                                            handleToggleAttendanceReminder={handleToggleAttendanceReminder}
                                            theme={theme}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Special Classes Table */}
                <div className={`shadow-xl rounded-xl overflow-hidden border mb-8 ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className={`px-6 py-4 ${
                        theme === 'dark' ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700'
                    }`}>
                        <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <FaCalendarDay className="text-white" />
                            </div>
                            Special Classes
                        </h3>
                        <p className="text-blue-50 text-sm mt-1">Manage one-time special class events</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm sm:text-base">
                            <thead>
                                <tr className={`text-white ${
                                    theme === 'dark' ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-500 to-blue-600'
                                }`}>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Class Name</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Time</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Date</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Instructor</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Spots</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Special Class</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Instructor Log in</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Attendance Check</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">All Attend</th>
                                    <th className="px-4 py-3 text-left font-bold text-xs sm:text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${
                                    theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'
                                }`}>
                                    {specialClasses.map(classItem => (
                                        <ClassRow
                                            key={classItem.id}
                                            classItem={classItem}
                                            editingClass={editingClass}
                                            setEditingClass={setEditingClass}
                                            coaches={coaches}
                                            isSpecial={true}
                                            handleSaveClass={handleSaveClass}
                                            handleDeleteClass={handleDeleteClass}
                                            isSecAdmin={isSecAdmin}
                                            handleToggleClass={handleToggleClass}
                                            handleToggleAttendanceReminder={handleToggleAttendanceReminder}
                                            theme={theme}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
        </LoadingOverlay>
    );
}